import { Head, Link } from '@inertiajs/react';

import {
    Btn,
    DesignFooter,
    DesignHeader,
    I,
    LawyerCardV,
    SectionHeader,
    UtilityBar,
    type LawyerCardData,
} from '@/Components/Design';

type StateProps = {
    state: { slug: string; name: string; abbreviation: string; sol: number | null; summary: string | null; lat: number | null; lng: number | null; lawyersCount: number };
    cities: Array<{ slug: string; name: string; population: number | null; lawyersCount?: number }>;
    lawyers: Array<{
        id: number;
        slug: string;
        name: string;
        firm: string | null;
        city: string | null;
        state: string | null;
        stateAbbr: string | null;
        yearsLicensed: number | null;
        languages: string[];
        reviewsAvg: number | null;
        reviewsCount: number;
        practiceAreas: Array<{ slug: string; name: string }>;
        featured: boolean;
        headshotKey: string | null;
    }>;
};

function initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase() || 'AN';
}

function hueOf(slug: string): number {
    let h = 0;
    for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) | 0;
    return Math.abs(h) % 6;
}

function toCard(l: StateProps['lawyers'][number], i: number): LawyerCardData {
    return {
        name: l.name,
        firm: l.firm ?? '',
        city: l.city ?? '',
        state: l.stateAbbr ?? '',
        rating: l.reviewsAvg ?? 0,
        reviews: l.reviewsCount,
        years: l.yearsLicensed ?? 0,
        areas: l.practiceAreas.map((p) => p.name),
        langs: l.languages,
        initials: initialsOf(l.name),
        hue: hueOf(l.slug) ?? i,
        featured: l.featured,
        href: `/${l.slug}`,
        headshotUrl: l.headshotKey ? `/cdn/lawyers/320/${l.headshotKey}.webp` : null,
    };
}

export default function State({ state, cities, lawyers }: StateProps) {
    return (
        <>
            <Head>
                <title>{`Personal injury lawyers in ${state.name} — ${state.lawyersCount.toLocaleString()} verified attorneys`}</title>
            </Head>
            <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>
                <UtilityBar />
                <DesignHeader active="By State" />

                {/* Hero */}
                <section data-state-section="hero" style={{ maxWidth: 1440, margin: '0 auto', padding: '40px 56px 32px' }}>
                    <nav aria-label="Breadcrumb">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-mute)', marginBottom: 18 }}>
                        <Link href="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
                        <span>/</span>
                        <Link href="/search" style={{ color: 'inherit', textDecoration: 'none' }}>By State</Link>
                        <span>/</span>
                        <span aria-current="page" style={{ color: 'var(--ink)' }}>{state.name}</span>
                    </div></nav>
                    <div data-state-hero-grid style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 48, alignItems: 'flex-end' }}>
                        <div>
                            <div style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ember)', marginBottom: 10, fontWeight: 600 }}>State directory</div>
                            <h1 style={{ fontSize: 72, lineHeight: 1.02, letterSpacing: '-0.025em', marginBottom: 18, fontFamily: 'var(--font-serif)', fontWeight: 500 }}>
                                Personal injury lawyers in {state.name}
                            </h1>
                            {state.summary && (
                                <p style={{ fontSize: 18, color: 'var(--ink-soft)', lineHeight: 1.55, maxWidth: 580, marginBottom: 24 }}>
                                    {state.summary}
                                </p>
                            )}
                            <div data-cta-row style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                <Btn kind="primary" size="lg" icon={I.search} href={`/search?state=${state.slug}`}>Find lawyers in {state.name}</Btn>
                                <Btn kind="secondary" size="lg" icon={I.phone} href="/get-matched">1-833-INJURY-1</Btn>
                            </div>
                        </div>
                        <div data-state-glyph style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 16, padding: 24, textAlign: 'center' }}>
                            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 96, fontWeight: 500, color: 'var(--ember)', letterSpacing: '-0.04em', lineHeight: 1 }}>{state.abbreviation}</div>
                            <div style={{ fontSize: 14, color: 'var(--ink-soft)', marginTop: 8 }}>{state.name}</div>
                        </div>
                    </div>
                </section>

                {/* Stats strip — only stats with real data; sub-captions removed (filler). */}
                {(() => {
                    const stats: [string, string][] = [];
                    if (state.lawyersCount > 0) stats.push([state.lawyersCount.toLocaleString(), 'attorneys']);
                    if (cities.length > 0) stats.push([`${cities.length}`, 'cities']);
                    if (state.sol) stats.push([`${state.sol} yr${state.sol === 1 ? '' : 's'}`, 'statute of limitations']);
                    if (stats.length === 0) return null;
                    return (
                        <section style={{ maxWidth: 1440, margin: '0 auto', padding: '0 56px 40px' }}>
                            <div data-state-stats style={{ display: 'grid', gridTemplateColumns: `repeat(${stats.length}, 1fr)`, border: '1px solid var(--rule)', borderRadius: 16, background: 'var(--card)', overflow: 'hidden' }}>
                                {stats.map(([v, l], i) => (
                                    <div key={l} style={{ padding: '28px 28px', borderLeft: i ? '1px solid var(--rule)' : 0 }}>
                                        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 40, lineHeight: 1, letterSpacing: '-0.02em', fontWeight: 500, marginBottom: 8 }}>{v}</div>
                                        <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 600 }}>{l}</div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    );
                })()}

                {/* Cities */}
                {cities.length > 0 && (
                    <section data-state-section="cities" style={{ maxWidth: 1440, margin: '0 auto', padding: '32px 56px' }}>
                        <SectionHeader eyebrow="By city" title={`Where in ${state.name}?`} />
                        <div data-state-cities-grid style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                            {cities.slice(0, 12).map((c) => (
                                <Link key={c.slug} href={`/${state.slug}/${c.slug}`} style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 12, padding: 18, textDecoration: 'none', color: 'inherit' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                                        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 500 }}>{c.name}</div>
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                                        {typeof c.lawyersCount === 'number' && c.lawyersCount > 0
                                            ? `${c.lawyersCount.toLocaleString()} attorney${c.lawyersCount === 1 ? '' : 's'}`
                                            : c.population
                                              ? `Pop. ${c.population.toLocaleString()}`
                                              : ''}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Featured attorneys */}
                {lawyers.length > 0 && (
                    <section data-state-section="featured" style={{ maxWidth: 1440, margin: '0 auto', padding: '32px 56px' }}>
                        <SectionHeader eyebrow={`In ${state.name}`} title="Recently active attorneys" action={<Btn kind="text" href={`/search?state=${state.slug}`}>See all →</Btn>} />
                        <div data-state-featured-grid style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
                            {lawyers.slice(0, 3).map((l, i) => (
                                <LawyerCardV key={l.id} data={toCard(l, i)} />
                            ))}
                        </div>
                    </section>
                )}

                <DesignFooter />
            </div>
        </>
    );
}
